import BaseComponent, { defineComponent } from '@/components/base';
import { ButtonStyles, FontStyle } from '@/components/style';
import Style from '@/components/customization-details/style';
import { fragranceMap, discontinuedFragranceMap } from '@/utils/fragrance-map';
import { goalsMap, discontinuedGoalsMap } from '@/utils/goals-map';
import { bodyMoistureMap } from '@/utils/body-moisture';
import useLogger from '@/service/logger-service';
import ColorSelection from '@/components/mini-quiz/color-selection';

useLogger();

/**
 * CustomizationDetails component: allows users to update their formula name, hair goals, and fragrance.
 *
 * Attributes:
 * - 'name' (string): The name of the profile.
 * - 'goals' (string): A comma-separated string of goal data keys.
 * - 'fragrance' (string): The data key of the selected fragrance.
 * - 'properties' (string): A JSON string representing the line item properties.
 * - 'items' (string): A JSON string representing the products in the order.
 */
export default class CustomizationDetails extends BaseComponent {
  static get observedAttributes() {
    return [
      'name',
      'goals',
      'hair-fragrance',
      'body-fragrance',
      'properties',
      'limited-fragrances',
      'profile-type',
      'body-moisture',
      'colors',
      'excluded-colors',
      'product-handle',
      'product-title',
      'hair-type'
    ];
  }

  onConstruct() {
    defineComponent('color-selection', ColorSelection);
    this.goals = [...goalsMap.hair];
    this.discontinuedGoals = [...discontinuedGoalsMap.hair];
    this.bodyMoisture = bodyMoistureMap.body;
  }
  afterMount() {
    this.shadowRoot
      .querySelector('#update-formula')
      ?.addEventListener('click', (event) => {
        event.preventDefault();
        const orderId = this.getAttribute('order-id');
        const lineItemKey = this.getAttribute('line-item-key');
        const profileType = this.profileType;
        if (this.handleSubmit) {
          event.target.disabled = true;
          this.handleSubmit({ orderId, profileType, lineItemKey });
        }
      });

    this.shadowRoot
      .querySelector('#revert-btn')
      ?.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.handleRevertCustomizations) {
          const orderId = this.getAttribute('order-id') || '';
          const lineItemKey = this.getAttribute('line-item-key') || '';
          this.handleRevertCustomizations({ orderId, lineItemKey });
        }
      });

    const fragranceSelector = this.shadowRoot.querySelector(
      '#hair-fragrance-selector'
    );
    if (fragranceSelector) {
      fragranceSelector.handleOptionClick = (option) => {
        this.updateSubmitBtnState();
      };
    }

    const bodyFragranceSelector = this.shadowRoot.querySelector(
      '#body-fragrance-selector'
    );
    if (bodyFragranceSelector) {
      bodyFragranceSelector.handleOptionClick = (option) => {
        this.updateSubmitBtnState();
      };
    }

    const goalsSelector = this.shadowRoot.querySelector('#goals-selector');
    if (goalsSelector) {
      goalsSelector.handleOptionClick = (option) => {
        this.updateSubmitBtnState();
      };
    }
    this.updateSubmitBtnState();
  }

  render() {
    return `
      ${FontStyle}
      ${ButtonStyles}
      ${Style}
      <section class="customization-details-wrapper">
        ${this.productTitle ? `<h2>${this.productTitle}</h2>` : ''}
        <h3>Update your formula name</h3><hr>
        <p class="current-selection">Current Selection: ${this.originalName}</p>
        <label>Function of: </label>
        <input value="${this.getAttribute(
          'name'
        )}" type="text" id="profile-name"/>

       ${
         this.profileType === 'hair' || this.profileType === 'mixed'
           ? ` <div class="customization-title-wrapper">${
               this.goalsError ? `<span class="error-icon">!</span>` : ''
             } <h3>Update your hair goals </h3><span class="error">${
               this.goalsError
             }</span></div><hr>
        <p class="current-selection">Current Selection: ${
          this.originalGoals
        }</p>

        <customization-multi-selector
          id="goals-selector"
          limit="5"
          selection='${JSON.stringify(this.selectedGoals)}'
        >
          <data name="options" type="json">
            ${JSON.stringify(this.goals)}
          </data>
        </customization-multi-selector>`
           : ''
       }

       ${
         this.profileType === 'body' || this.profileType === 'mixed'
           ? ` <div class="customization-title-wrapper"><h3>Update your body moisture </h3></div><hr>
          <p class="current-selection">Current Selection: ${
            this.originalBodyMoisture
          }</p>

          <customization-selector
          id="body-moisture-selector"
          limit="1"
          selection='${JSON.stringify({
            name: this.originalBodyMoisture,
            dataKey: this.originalBodyMoisture
              .toLocaleLowerCase()
              .replace(' ', '-')
          })}'
          >
            <data name="options" type="json">
              ${JSON.stringify(this.bodyMoisture)}
            </data>
          </customization-selector>`
           : ''
       }

      ${
        this.profileType === 'hair' || this.profileType === 'mixed'
          ? ` <div class="customization-title-wrapper">${
              this.hairFragranceError ? `<span class="error-icon">!</span>` : ''
            } <h3>Update your hair fragrance </h3><span class="error">${
              this.hairFragranceError
            }</span></div><hr>
        <p class="current-selection">Current Selection: ${
          this.originalHairFragrance
        }</p>

        <customization-selector
          id="hair-fragrance-selector"
          selection='${JSON.stringify(this.selectedHairFragrance)}'
        >
          <data name="options" type="json">
            ${JSON.stringify(this.hairFragrances)}
          </data>
        </customization-selector>`
          : ''
      }

      ${
        this.profileType === 'body' || this.profileType === 'mixed'
          ? ` <div class="customization-title-wrapper">${
              this.bodyFragranceError ? `<span class="error-icon">!</span>` : ''
            } <h3>Update your body fragrance </h3><span class="error">${
              this.bodyFragranceError
            }</span></div><hr>
        <p class="current-selection">Current Selection: ${
          this.originalBodyFragrance
        }</p>

        <customization-selector
          id="body-fragrance-selector"
          selection='${JSON.stringify(this.selectedBodyFragrance)}'
        >
          <data name="options" type="json">
            ${JSON.stringify(this.bodyFragrances)}
          </data>
        </customization-selector>`
          : ''
      }

      ${
        this.colors
          ? `
          <div class="color-selector-wrapper">
            <h3>Update Color</h3><hr>
            <p class="current-selection">Current Selection: ${this.preselectedColor}</p>
            <p class="current-selection">New Selection: <span id="new-color-selection">${this.preselectedColor}</span></p>
            <color-selection
              excluded-colors="${this.excludedColors}"
              colors='${this.colors}'
              product="${this.productHandle}"
              preselected-color='${this.preselectedColor}'
              hide-fragrance-text
              hair-type="${this.hairType}"
            >
            </color-selection>
          </div>`
          : ''
      }

        <div class="buttons-wrapper">
          <button class="cta cta-2 revert-btn" id="revert-btn">Revert</button>
          <button class="cta cta-1 update-formula" id="update-formula" >Update Formula</button>
        </div>
      </section>
    `;
  }

  /**
   * Retrieves available fragrances, filtering by the 'limited-fragrances' attribute.
   *
   * @returns {Array} An array of available fragrances.
   */
  get hairFragrances() {
    const filteredFragrances =
      this.getAttribute('limited-fragrances') === '1'
        ? fragranceMap.filter((item) => !item.coreOnly)
        : fragranceMap;

    return filteredFragrances.map(({ name, dataKey }) => ({ name, dataKey }));
  }

  get bodyFragrances() {
    const filteredFragrances = fragranceMap;

    return filteredFragrances.map(({ name, dataKey }) => ({ name, dataKey }));
  }

  /**
   * Retrieves available fragrances, filtering by the 'limited-fragrances' attribute.
   *
   * @returns {Array} An array of available fragrances.
   */
  get fragrances() {
    const filteredFragrances =
      this.getAttribute('limited-fragrances') === '1'
        ? fragranceMap.filter((item) => !item.coreOnly)
        : fragranceMap;

    return filteredFragrances.map(({ name, dataKey }) => ({ name, dataKey }));
  }

  /**
   * Retrieves the list of colors from the colors attribute
   *
   * @returns {String} A stringified array of colors.
   */
  get colors() {
    return this.getAttribute('colors');
  }

  /**
   * Retrieves the preselected color from the preselected-color attribute.
   *
   * @returns {String}
   */
  get preselectedColor() {
    return this.getAttribute('preselected-color');
  }

  /**
   * Retrieves the list of excluded colors from the excluded-colors attribute
   *
   * @returns {String} A comma-separated string of excluded colors.
   */
  get excludedColors() {
    return this.getAttribute('excluded-colors');
  }

  get hairType() {
    return this.getAttribute('hair-type');
  }

  /**
   * Retrieves the product handle from the product-handle attribute.
   *
   * @returns {String}
   */
  get productHandle() {
    return this.getAttribute('product-handle');
  }

  /**
   * Retrieves the product name from the product-name attribute.
   *
   * @returns {String}
   */
  get productTitle() {
    return this.getAttribute('product-title');
  }

  /**
   * Gets the selected fragrance based on the 'fragrance' attribute.
   * This corresponds to the fragrance selected by the user in the fragrance selector.
   *
   * @returns {Object|string} The selected fragrance object if found, otherwise an empty string.
   */
  get selectedHairFragrance() {
    const fragranceKey = this.hairFragrance;
    const selected =
      this.hairFragrances?.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      ) || '';
    return selected;
  }

  get selectedBodyFragrance() {
    const fragranceKey = this.getAttribute('body-fragrance');
    const selected =
      this.bodyFragrances?.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      ) || '';
    return selected;
  }

  /**
   * Gets the selected goals based on the 'goals' attribute.
   * This corresponds to the goals selected by the user in the goals selector.
   *
   * @returns {Array} An array of selected goal objects whose dataKey matches the keys specified in the 'goals' attribute.
   */
  get selectedGoals() {
    const goalsKeys = this.getAttribute('goals').split(',');
    const selected = this.goals?.filter((goal) => {
      return goalsKeys.includes(goal.dataKey);
    });
    return selected;
  }

  get profileType() {
    return this.getAttribute('profile-type');
  }

  get originalBodyMoisture() {
    return this.getAttribute('body-moisture');
  }

  /**
   * Retrieves the 'items' attribute and parses it as JSON.
   * This corresponds to the products in the order.
   *
   * @returns {Array|Object} The parsed JSON representation of the items attribute.
   */
  get items() {
    return JSON.parse(this.getAttribute('items'));
  }

  /**
   * Gets the original name attribute.
   * This is what is shown as the current selection in the name input field.
   *
   * @returns {string} The value of the 'name' attribute.
   */
  get originalName() {
    return this.getAttribute('name');
  }

  /**
   * Retrieves the original goals from the order based on the 'goals' attribute.
   * This is what is shown as the current selection in the goals selector.
   * It looks up the goals in both the goals map and discontinued goals, then returns the name.
   *
   * @returns {string} A comma-separated string of goal names.
   */
  get originalGoals() {
    const goalsKeys = this.getAttribute('goals').split(',');
    const allGoals = [...(this.goals || []), ...(this.discontinuedGoals || [])];
    const goalsArray = allGoals?.filter((goal) => {
      return goalsKeys.includes(goal.dataKey);
    });
    return goalsArray?.map((goal) => goal.name).join(', ');
  }

  /**
   * Gets the name of the original fragrance based on the 'fragrance' attribute.
   * This is what is shown as the current selection in the fragrance selector.
   * It looks up the fragrance in both the fragrances map and discontinued fragrances, then returns the name.
   *
   * @returns {string} The name of the original fragrance. If the fragrance is not found, returns 'None'.
   */
  get originalHairFragrance() {
    const fragranceKey = this.hairFragrance;
    const allFragrances = [
      ...(this.hairFragrances || []),
      ...(discontinuedFragranceMap || [])
    ];
    const selected =
      allFragrances?.find((fragrance) => fragrance.dataKey === fragranceKey) ||
      '';
    return selected?.name || 'None';
  }

  get originalBodyFragrance() {
    const fragranceKey = this.getAttribute('body-fragrance');
    const selected =
      this.bodyFragrances?.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      ) || '';
    return selected?.name || 'None';
  }
  /**
   * Checks whether the current fragrance is available. Returns an error message if it is not.
   *
   * @returns {string} Error message if the fragrance is not available, otherwise an empty string.
   */
  get hairFragranceError() {
    const fragranceKey = this.hairFragrance ? this.hairFragrance : 'None';
    return fragranceKey === 'None' ||
      discontinuedFragranceMap?.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      ) ||
      !this.hairFragrances.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      )
      ? "Current option isn't available. Please select another one."
      : '';
  }

  get bodyFragranceError() {
    const fragranceKey = this.getAttribute('body-fragrance');
    return fragranceKey === 'None' ||
      discontinuedFragranceMap?.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      ) ||
      !this.bodyFragrances.find(
        (fragrance) => fragrance.dataKey === fragranceKey
      )
      ? "Current option isn't available. Please select another one."
      : '';
  }

  /**
   * Checks if any of the current goals are discontinued and returns an error message if so.
   *
   * @returns {string} Error message if any of the current goals are discontinued, otherwise an empty string.
   */
  get goalsError() {
    const goalsKeys = this.getAttribute('goals').split(',');
    const discontinuedGoalsFound = this.discontinuedGoals
      ?.map((goal) => goal.dataKey)
      ?.some((goalKey) => goalsKeys.includes(goalKey));

    if (discontinuedGoalsFound) {
      return 'One or more of the selected goals are discontinued. Please select another one.';
    }
    return '';
  }

  /**
   * Enable or disable the 'update your formula' button based on the goals and fragrance selection.
   *
   * @returns {Void}
   */
  updateSubmitBtnState() {
    const goalsSelector = this.shadowRoot.querySelector('#goals-selector');
    const hairFragranceSelector = this.shadowRoot.querySelector(
      '#hair-fragrance-selector'
    );
    const bodyFragranceSelector = this.shadowRoot.querySelector(
      '#body-fragrance-selector'
    );

    const submitBtn = this.shadowRoot.querySelector('#update-formula');

    const goalsSelection = JSON.parse(
      goalsSelector?.getAttribute('selection') || '[]'
    );
    const hairFragranceSelection =
      hairFragranceSelector?.getAttribute('selection') || '""';
    const bodyFragranceSelection =
      bodyFragranceSelector?.getAttribute('selection') || '""';

    let validateFragrance = false;
    let validateGoals = true;

    if (this.profileType === 'hair') {
      validateFragrance = hairFragranceSelection != '""';
      validateGoals = goalsSelection.length > 0;
    } else if (this.profileType === 'body') {
      validateFragrance = bodyFragranceSelection != '""';
    } else {
      validateFragrance =
        hairFragranceSelection != '""' && bodyFragranceSelection != '""';
    }

    submitBtn.disabled = !(validateGoals && validateFragrance);
  }

  get hairFragrance() {
    const hairFragranceAttribute = this.getAttribute('hair-fragrance');
    if (hairFragranceAttribute !== null) {
      return hairFragranceAttribute.trim();
    }
    return hairFragranceAttribute;
  }
}
